// src/utils/exportCustomXlsx.ts
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {IArtifactFlat} from "./export-flat";

export interface ExportCustomOptions {
    sheetName?: string;
    fileName?: string;
    colWidths?: number[];
    merges?: XLSX.Range[];
    /** zero‐based column indices to render in bold */
    boldColumns?: number[];
}

export function exportXlsx(artifact: IArtifactFlat): void {
    const data = prepareArtifactForXlsExport(artifact);
    exportCustomXlsx(data.rows, data.options);
};

export function prepareArtifactForXlsExport(artifact: IArtifactFlat): any {
    const rows = [
        ['ID', artifact.id],
        ['Title', artifact.title],
        ['Description', artifact.description],
        ['Estimated Value', artifact.estimatedValue],
        ['Dimensions', artifact.dimensions],
        ['Weight', artifact.weight],
        ['Age', artifact.age],
        ['Material', artifact.material],
        ['Style', artifact.style],
        ['Origin', artifact.origin],
        ['Condition', artifact.condition],
        ['Artist', artifact.artist],
        ['Artist Bio', artifact.artistBio],
      //  ['Photos', artifact.photos],
    ];

    const options = {
        sheetName: 'Artifact',
        fileName: `${artifact.title}.xlsx`,
        colWidths: [10, 20],  // approx. character widths
        merges: [],
        boldColumns: [1]
    };

    return {rows, options};
}

export function exportArtifactList(artifacts: IArtifactFlat[], fileName: string): {rows: any, options: ExportCustomOptions} {
    const rows = [
        [
            'ID',
            'Title',
            'Description',
            'Estimated Value',
            'Dimestions',
            'Weight',
            'Age',
            'Material',
            'Style',
            'Origin',
            'Condition',
            'Artist',
            'Artist Bio',
           // 'Photos',
        ],
        ...artifacts.map(artifact => {
            return [
                artifact.id,
                artifact.title,
                artifact.description,
                artifact.estimatedValue,
                artifact.dimensions,
                artifact.weight,
                artifact.age,
                artifact.material,
                artifact.style,
                artifact.origin,
                artifact.condition,
                artifact.artist,
                artifact.artistBio,
               // artifact.photos,
            ];
        })
    ];

    const options = {
        sheetName: 'Artifact',
        fileName: `${fileName}.xlsx`,
        colWidths: [10, 20],  // approx. character widths
        merges: [],
        boldColumns: [1]
    };

    return {rows, options};
}

export function exportCustomXlsx(
    aoa: (string|number|boolean|Date|null)[][],
    options: ExportCustomOptions = {}
): void {
    const blob = new Blob([generateXlsx(aoa, options)], {
        type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(blob, options.fileName);
}

export function generateXlsx(aoa: (string|number|boolean|Date|null)[][], options: ExportCustomOptions = {}): any {
    const {
        sheetName = 'Sheet1',
        colWidths,
        merges,
        boldColumns = [],
    } = options;

    // 1) AOA → worksheet
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(aoa);

    // 2) apply column widths
    if (colWidths) {
        ws['!cols'] = colWidths.map(w => ({ wch: w }));
    }

    // 3) apply merges
    if (merges) {
        ws['!merges'] = merges;
    }

    // 4) inject bold style into each requested column
    if (boldColumns.length) {
        const range = XLSX.utils.decode_range(ws['!ref']!);
        boldColumns.forEach(colIdx => {
            for (let row = range.s.r; row <= range.e.r; ++row) {
                // build cell address, e.g. {c:2,r:5} → "C6"
                const cellAddress = XLSX.utils.encode_cell({ c: colIdx, r: row });
                const cell = ws[cellAddress];
                if (cell && typeof cell.v !== 'undefined') {
                    // ensure the .s object exists
                    cell.s = cell.s || {};
                    // apply bold font
                    cell.s.font = { ...(cell.s.font||{}), bold: true };
                }
            }
        });
    }

    // 5) pack into a workbook
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // 6) write with cellStyles enabled
    return XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
    });
}
